import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Container,
  Tooltip,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import TimelineIcon from '@mui/icons-material/Timeline';

import {
  useGetPlmCurtailmentTasksByResponseIdQuery,
  useGetPlmCurtailmentTasksByResponseIdSubSubscription,
} from '../../../../../types/generated-types';
import {
  handleModelUpdateEvent,
  updateCacheFromSubscriptionEvent,
} from '../../../../../helpers/subscriptionUtils';

// CSS keyframes for animations
const keyframesStyle = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes slideIn {
    from { transform: translateX(-20px); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }
  
  @keyframes rotateGradient {
    0% { background-position: 200% 50%; }
    100% { background-position: 0% 50%; }
  }
  
  .timeline-bar {
    animation: fadeIn 0.3s ease-out;
  }
  
  .device-row {
    animation: slideIn 0.3s ease-out;
  }
`;

// Helper function to get status color
const getStatusColor = (status: string) => {
  const baseColor = '#666';
  switch (status.toLowerCase()) {
    case 'scheduled':
      return '#2196f3'; // Blue - represents future/pending
    case 'active':
      return '#4caf50'; // Green - represents running/working
    case 'completed':
      return '#8bc34a'; // Light green - represents success
    case 'verifying':
      return '#ff9800'; // Orange - represents checking/verification
    case 'transitioning':
      return '#9c27b0'; // Purple - represents state change
    case 'aborted':
      return '#ff5722'; // Deep orange - represents stopped/cancelled
    case 'missed':
      return '#795548'; // Brown - represents missed/expired
    case 'error':
      return '#f44336'; // Red - represents failure/error
    default:
      return baseColor;
  }
};

// Helper function to get display status (for grouping similar statuses)
const getDisplayStatus = (status: string) => {
  if (['pending', 'stopping_aborted', 'stopping_completed'].includes(status)) {
    return 'verifying';
  }
  if (['execute_retry', 'abort_retry', 'complete_retry'].includes(status)) {
    return 'transitioning';
  }
  return status;
};

// Helper function to get detailed status for hover tooltips
const getDetailedStatus = (status: string) => {
  if (['pending', 'stopping_aborted', 'stopping_completed'].includes(status)) {
    let targetState = 'unknown';
    if (status === 'stopping_completed') targetState = 'completed';
    else if (status === 'stopping_aborted') targetState = 'aborted';
    else if (status === 'pending') targetState = 'active';
    return `verifying (${targetState})`;
  }
  if (['execute_retry', 'abort_retry', 'complete_retry'].includes(status)) {
    let targetState = 'unknown';
    if (status === 'complete_retry') targetState = 'completed';
    else if (status === 'abort_retry') targetState = 'aborted';
    else if (status === 'execute_retry') targetState = 'active';
    return `transitioning (${targetState})`;
  }
  return status;
};

// Helper function to get status styles including animations
const getStatusStyles = (status: string) => {
  const baseColor = getStatusColor(status);

  if (status === 'active') {
    return {
      background: `linear-gradient(90deg, 
        ${baseColor} 0%, 
        #ffeb3b 15%, 
        #4caf50 30%, 
        #ffeb3b 45%, 
        ${baseColor} 60%, 
        #ffeb3b 75%, 
        #4caf50 90%, 
        ${baseColor} 100%)`,
      backgroundSize: '200% 100%',
      animation: 'rotateGradient 1.5s linear infinite',
    };
  }

  return {
    backgroundColor: baseColor,
  };
};

// Helper function to format duration
const formatDuration = (start: number, end: number) => {
  const duration = end - start;
  const minutes = Math.floor(duration / 60000);
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (hours > 0) {
    return `${hours}h ${remainingMinutes}m`;
  }
  return `${minutes}m`;
};

interface PlmCurtailmentTask {
  _id: string;
  plmCurtailmentActionId: string;
  plmEventResponseId: string;
  batchId: number;
  propertyId: string;
  unitId: string;
  deviceId: string;
  deviceType: string;
  scheduledStart: number;
  scheduledEnd: number;
  startedAt?: number | null;
  verifiedAt?: number | null;
  verifyAttempts?: number | null | undefined;
  endedAt?: number | null;
  status: string;
  statusMessage?: string | null;
  estimatedReductionKW?: number | null;
  device?: {
    _id: string;
    name?: string | null;
    deviceId?: string | null;
    unit?: {
      _id: string;
      name?: string | null;
    } | null;
  } | null;
  cachedDeviceState?: {
    type: string;
    coolSetpoint?: number | null;
    heatSetpoint?: number | null;
    isOn?: boolean | null;
    operatingMode?: string | null;
    fanMode?: string | null;
  } | null;
  targetedAdjustments?: {
    type: string;
    coolSetpoint?: number | null;
    heatSetpoint?: number | null;
    isOn?: boolean | null;
    operatingMode?: string | null;
    fanMode?: string | null;
  } | null;
}

interface GroupedDevice {
  deviceId: string;
  unitId: string;
  deviceType: string;
  deviceDisplayName?: string | null;
  unitName?: string | null;
  tasks: PlmCurtailmentTask[];
}

const PlmResponseTimelineView: React.FC = () => {
  const { id: plmEventResponseId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo =
    searchParams.get('returnTo') ||
    `/plm-event-responses/${plmEventResponseId}`;
  const unitId = searchParams.get('unitId');
  const [tasks, setTasks] = useState<PlmCurtailmentTask[]>([]);

  // Query for PLM curtailment tasks
  const {
    data,
    refetch: reloadCurtailmentTasks,
    loading,
    error,
  } = useGetPlmCurtailmentTasksByResponseIdQuery({
    variables: { plmEventResponseId: plmEventResponseId! },
    fetchPolicy: 'network-only',
  });

  // Subscribe to real-time updates
  useGetPlmCurtailmentTasksByResponseIdSubSubscription({
    variables: { plmEventResponseId: plmEventResponseId! },
    fetchPolicy: 'no-cache',
    onData: handleModelUpdateEvent(async ({ data }) => {
      if (data?.data?.plmCurtailmentTasksByResponseIdEvents) {
        await reloadCurtailmentTasks();
      }
    }),
  });

  // Update tasks when query data changes
  useEffect(() => {
    if (data?.plmCurtailmentTasksByResponseId) {
      let filteredTasks = data.plmCurtailmentTasksByResponseId;

      // Filter by unitId if provided
      if (unitId) {
        filteredTasks = filteredTasks.filter((task) => task.unitId === unitId);
      }

      setTasks(filteredTasks);
    }
  }, [data, unitId]);

  // Calculate timeline bounds
  const earliestStart = Math.min(...tasks.map((task) => task.scheduledStart));
  const latestEnd = Math.max(...tasks.map((task) => task.scheduledEnd));
  const totalDuration = latestEnd - earliestStart;

  // Group tasks by device
  const tasksByDevice: Record<string, GroupedDevice> = {};
  tasks.forEach((task) => {
    const deviceKey = `${task.deviceId}-${task.unitId}`;
    if (!tasksByDevice[deviceKey]) {
      tasksByDevice[deviceKey] = {
        deviceId: task.deviceId,
        unitId: task.unitId,
        deviceType: task.deviceType,
        deviceDisplayName: task.device?.name,
        unitName: task.device?.unit?.name,
        tasks: [],
      };
    }
    tasksByDevice[deviceKey].tasks.push(task);
  });

  // Sort devices by unit name for consistent ordering
  const sortedDevices = Object.values(tasksByDevice).sort((a, b) =>
    (a.unitName || a.unitId).localeCompare(b.unitName || b.unitId),
  );

  // Helper function to get status color
  const getStatusColor = (status: string) => {
    const baseColor = '#666';
    switch (status.toLowerCase()) {
      case 'scheduled':
        return '#2196f3';
      case 'pending':
        return '#ff9800';
      case 'active':
        return '#4caf50';
      case 'stopping_aborted':
      case 'stopping_completed':
        return '#9c27b0';
      case 'completed':
        return '#4caf50';
      case 'error':
        return '#f44336';
      case 'execute_retry':
      case 'abort_retry':
      case 'complete_retry':
        return '#ff9800';
      default:
        return baseColor;
    }
  };

  // Helper function to calculate position and width for timeline bars
  const getTimelineBarProps = (task: PlmCurtailmentTask) => {
    const left = ((task.scheduledStart - earliestStart) / totalDuration) * 100;
    const width =
      ((task.scheduledEnd - task.scheduledStart) / totalDuration) * 100;

    return { left: `${left}%`, width: `${width}%` };
  };

  const handleReturnToEventResponse = () => {
    navigate(returnTo);
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box
          component="div"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          width="100%"
          height="100%"
        >
          <Box sx={{ marginBottom: '2rem' }}>
            <CircularProgress color="secondary" size="3rem" />
          </Box>
          <Box>
            <Typography variant="h4" color="secondary">
              Preparing your data...
            </Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mt: 2 }}>
          Error loading timeline data: {error.message}
        </Alert>
      </Container>
    );
  }

  if (!tasks.length) {
    return (
      <Container maxWidth="xl">
        <Alert severity="info" sx={{ mt: 2 }}>
          No PLM curtailment tasks found for this event response.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      {/* Inject CSS keyframes for animations */}
      <style>{keyframesStyle}</style>

      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleReturnToEventResponse}
          variant="outlined"
          sx={{ flexShrink: 0 }}
        >
          Return
        </Button>
        <Box display="flex" alignItems="center" gap={1}>
          <TimelineIcon color="info" />
          <Typography variant="h4">PLM Response Timeline</Typography>
        </Box>
      </Box>

      {/* Summary */}
      <Box mb={3}>
        <Typography variant="body1" color="text.secondary">
          Timeline shows {tasks.length} PLM curtailment tasks across{' '}
          {sortedDevices.length} devices from{' '}
          {format(earliestStart, 'MMM dd, yyyy HH:mm')} to{' '}
          {format(latestEnd, 'MMM dd, yyyy HH:mm')}
        </Typography>
      </Box>

      {/* Timeline Container */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 3,
          position: 'relative',
          minHeight: '400px',
          backgroundColor: '#fafafa',
        }}
      >
        {/* Time markers */}
        <Box sx={{ position: 'relative', mb: 3, marginLeft: '200px' }}>
          {Array.from({ length: 6 }, (_, i) => {
            const time = earliestStart + (totalDuration * i) / 5;
            const left = (i / 5) * 100;
            return (
              <Box
                key={i}
                sx={{
                  position: 'absolute',
                  left: `${left}%`,
                  transform: 'translateX(-50%)',
                  textAlign: 'center',
                  zIndex: 10,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {format(time, 'HH:mm')}
                </Typography>
                <Box
                  sx={{
                    width: '1px',
                    height: '8px',
                    backgroundColor: '#ccc',
                    mx: 'auto',
                    mt: 0.5,
                  }}
                />
              </Box>
            );
          })}
        </Box>

        {/* Device rows with timeline bars */}
        <Box sx={{ position: 'relative' }}>
          {sortedDevices.map((device, deviceIndex) => (
            <Box
              key={`${device.deviceId}-${device.unitId}`}
              className="device-row"
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 2,
                minHeight: '50px',
              }}
            >
              {/* Device label */}
              <Box
                sx={{
                  width: '200px',
                  pr: 2,
                  textAlign: 'right',
                  flexShrink: 0,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}
                >
                  {device.deviceType}
                </Typography>
                {device.deviceDisplayName && (
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{ mt: 0.5 }}
                  >
                    Name: {device.deviceDisplayName}
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Unit: {device.unitName || `Unit ${device.unitId}`}
                </Typography>
              </Box>

              {/* Timeline bars for this device */}
              <Box
                sx={{
                  position: 'relative',
                  height: '40px',
                  flexGrow: 1,
                  backgroundColor: '#f0f0f0',
                  borderRadius: '4px',
                  backgroundImage: `
                    linear-gradient(90deg, rgba(0,0,0,0.05) 1px, transparent 1px),
                    linear-gradient(0deg, rgba(0,0,0,0.05) 1px, transparent 1px)
                  `,
                  backgroundSize: '100px 40px, 100% 20px',
                  overflow: 'hidden',
                  // Ensure timeline starts at the left edge of this container
                  left: 0,
                }}
              >
                {device.tasks.map((task, taskIndex) => {
                  const { left, width } = getTimelineBarProps(task);
                  return (
                    <Tooltip
                      key={task._id}
                      title={
                        <Box>
                          <Typography variant="body2">
                            <strong>Batch {task.batchId}</strong>
                          </Typography>
                          <Typography variant="body2">
                            Start:{' '}
                            {format(task.scheduledStart, 'MMM dd, yyyy HH:mm')}
                          </Typography>
                          <Typography variant="body2">
                            End:{' '}
                            {format(task.scheduledEnd, 'MMM dd, yyyy HH:mm')}
                          </Typography>
                          <Typography variant="body2">
                            Duration:{' '}
                            {formatDuration(
                              task.scheduledStart,
                              task.scheduledEnd,
                            )}
                          </Typography>
                          <Typography variant="body2">
                            Status: {getDetailedStatus(task.status)}
                          </Typography>
                          {task.estimatedReductionKW && (
                            <Typography variant="body2">
                              Est. Reduction: {task.estimatedReductionKW} kW
                            </Typography>
                          )}
                          {task.statusMessage && (
                            <Typography variant="body2">
                              Message: {task.statusMessage}
                            </Typography>
                          )}
                        </Box>
                      }
                      arrow
                    >
                      <Box
                        className="timeline-bar"
                        sx={{
                          position: 'absolute',
                          left,
                          width,
                          height: '32px',
                          top: '4px',
                          ...getStatusStyles(task.status),
                          borderRadius: '4px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            transform: 'scaleY(1.1)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                          },
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Legend */}
      <Paper elevation={1} sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Status Legend
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={2}>
          {[
            { status: 'scheduled', label: 'Scheduled' },
            { status: 'active', label: 'Active' },
            { status: 'completed', label: 'Completed' },
            { status: 'verifying', label: 'Verifying' },
            { status: 'transitioning', label: 'Transitioning' },
            { status: 'aborted', label: 'Aborted' },
            { status: 'missed', label: 'Missed' },
            { status: 'error', label: 'Error' },
          ].map(({ status, label }) => (
            <Box key={status} display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: '16px',
                  height: '16px',
                  ...getStatusStyles(status),
                  borderRadius: '2px',
                }}
              />
              <Typography variant="body2">{label}</Typography>
            </Box>
          ))}
        </Box>
      </Paper>
    </Container>
  );
};

export default PlmResponseTimelineView;
